import type { Workflow } from '../../../api/workflows-client/workflows-client.type';
import { formatDate } from '../../../helpers/date-time-helpers/formatting/formatting';
import { ProcessStore } from '../../../store/process-store/process-store';
import type { ProcessContextType, ProcessScope } from '../context/process.context.type';

/**
 * Determines the appropriate workflow and team parameters based on the provided process scope and workflows.
 *
 * @param {ProcessScope} params - The process scope containing workflow, board, team, and configuration settings
 * @param {Workflow[] | undefined} workflows - Array of available workflows that can be applied
 * @returns {{paramWorkflow: Workflow | null, teamParam: Team | null}}  An object containing the resolved workflow parameter and team parameter
 *   - paramWorkflow: The selected workflow based on scope settings, or null if not applicable
 *   - teamParam: The team to be used, or null if workflow takes precedence
 */
function getWorkflowAndTeamParams(params: ProcessScope, workflows: Workflow[] | undefined) {
  const { useWorkflow, workflow, board, team, useGroups } = params;

  const paramWorkflow = useWorkflow
    ? workflow
    : board && !useGroups
      ? (workflows?.find((w) => w.subprojects.includes(board?.id)) ?? null)
      : null;

  const teamParam = useWorkflow ? null : paramWorkflow !== null ? null : team;

  return { paramWorkflow, teamParam };
}

/**
 * Creates a human-readable string representation of a process scope.
 * The string includes the relevant components of the scope (workflow/team, board, epic, taskType)
 * separated by " | " delimiters.
 *
 * @param {ProcessScope} scope - The process scope object to stringify
 * @param {boolean} showDates - Whether to show the dates in the string
 * @returns {string} A formatted string representation of the process scope
 * @example
 */
function stringifyProcessScope(scope: ProcessScope, showDates = false): string {
  let result = '';
  if (scope.useWorkflow) {
    result += `${scope.workflow?.name}`;
  } else {
    result += `${scope.team?.name}`;
  }

  if (scope.board) {
    result += ` | ${scope.board?.name}`;
  }

  if (scope.workPeriod) {
    result += ` | ${scope.workPeriod?.name}`;
  }

  if (scope.epic) {
    result += ` | ${scope.epic?.name}`;
  }

  if (scope.taskTypes) {
    result += ` | ${scope.taskTypes.join(', ')}`;
  }

  if (scope.task?.title) {
    result += ` | ${scope.task?.title}`;
  }

  if (showDates && scope.startDate && scope.endDate) {
    result += ` | ${formatDate(scope.startDate)} - ${formatDate(scope.endDate)}`;
  }

  return result;
}

/**
 * Resets the process task details in both the context and store.
 * This function is used when navigating between different views or selecting new workflows
 * to ensure that any previously selected workflow or event data is cleared.
 * This clears task details selections for both the left and right graphs as well as the event scope.
 *
 * @param {ProcessContextType} processContext - The process context containing workflow and event scope state
 * @param {ProcessStore} processStore - The process store containing workflow and event scope state
 */
function resetProcessTaskDetails(processContext: ProcessContextType, processStore: ProcessStore): void {
  const {
    setWorkflow: setLeftWorkflow,
    setRightWorkflow,
    eventScope,
    setEventScope,
    rightEventScope,
    setRightEventScope,
  } = processContext;
  const {
    setWorkflow: setLeftWorkflowStore,
    setRightWorkflow: setRightWorkflowStore,
    setEventScope: setEventScopeStore,
    setRightEventScope: setRightEventScopeStore,
    eventScope: eventScopeStore,
    rightEventScope: rightEventScopeStore,
  } = processStore;

  // need to reset the selected process workflows and edge ids
  setLeftWorkflow(null);
  setRightWorkflow(null);
  setLeftWorkflowStore(null);
  setRightWorkflowStore(null);
  setEventScope({
    ...eventScope,
    selectedEdgeId: null,
  });
  setRightEventScope({
    ...rightEventScope,
    selectedEdgeId: null,
  });
  setEventScopeStore({
    ...eventScopeStore,
    selectedEdgeId: null,
  });
  setRightEventScopeStore({
    ...rightEventScopeStore,
    selectedEdgeId: null,
  });
}

export { getWorkflowAndTeamParams, resetProcessTaskDetails, stringifyProcessScope };
